import React, { useState, useRef, useEffect } from "react";
import BtnParentFrame from './buttons';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { usePostgresUser } from './utils/PostgresUserContext';
import LevelsNavDropDown from './levelsNavDropDown'; // Import the new component
import logoImage from '../images/landingPageAssets/onikanji-logo.png';
import { FaCoins } from 'react-icons/fa';
import { HiDotsVertical } from 'react-icons/hi';

import { isAndroidApp, isInstalledApp } from '../services/appContext';
const Navbar = ({ user, currentPath, subscriptionStatus, darkMode = false }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { logout } = usePostgresUser();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUtilityMenuOpen, setIsUtilityMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const utilityMenuRef = useRef(null);

    // Check if current page is the coin shop
    const isOnCoinShop = pathname === '/get-coins';

    // Determine theme classes based on current page - coin shop now uses light theme
    const navTheme = darkMode ? 'text-white' : 'text-black';

    // Button theme
    const buttonTheme = 'bg-darkOrangeColor';

    const handleSignOut = async () => {
        await logout();
        navigate('/login');
    };

    const handleNavigation = (path) => {
        setIsMenuOpen(false);
        setIsUtilityMenuOpen(false);
        navigate(path, { state: { previousPath: currentPath } });
    };

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            if (utilityMenuRef.current && !utilityMenuRef.current.contains(event.target)) {
                setIsUtilityMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const NavItem = ({ to, children }) => (
        <NavLink
            to={to}
            state={{ previousPath: currentPath }}
            className={({ isActive }) =>
                `px-4 py-2 rounded hover:underline focus:outline-none font-semibold ${
                    isActive ? 'text-darkOrangeColor' : ''
                }`
            }
        >
            {children}
        </NavLink>
    );

    return (
        <nav className={`${navTheme} p-2 relative transition-colors duration-300 ${darkMode ? 'bg-gray-900' : ''}`} role="navigation">
            <div className="container mx-auto flex justify-between items-center">
                <div className="flex-shrink-0">
                    <img 
                        src={logoImage} 
                        className="h-12 max-sm:h-10 lg:h-20 cursor-pointer" 
                        alt="OniKanji Logo" 
                        onClick={() => navigate('/home')} 
                    />
                </div>

                {/* Desktop Menu */}
                <div className="hidden lg:flex flex-grow justify-center">
                    <NavItem to="/home">Home</NavItem>
                    <NavItem to="/kanji">Kanji</NavItem>
                    <NavItem to="/kana-review">Kana</NavItem>
                    <NavItem to="/immersion-hub/my-library">My Library</NavItem>
                    <NavItem to="/leaderboards">Leaderboards</NavItem>
                    <NavItem to="/forum">Forum</NavItem>
                    <NavItem to="/my-stats">My Stats</NavItem>
                    {subscriptionStatus !== 'active' && !isAndroidApp() && <NavItem to="/pricing">Pricing</NavItem>}
                </div>

                {/* Desktop Coin Balance and Utility Menu */}
                <div className="hidden lg:flex items-center space-x-3">
                    {user && user.coins !== undefined && (
                        <div
                            onClick={() => { if (!isAndroidApp()) handleNavigation('/get-coins'); }}
                            className="flex items-center cursor-pointer px-3 py-1.5 rounded border border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:bg-opacity-10 transition-all duration-200"
                        >
                            <FaCoins className="mr-1.5" />
                            <span className="font-medium">{user.coins}</span>
                        </div>
                    )}
                    
                    {user ? (
                        <div className="relative" ref={utilityMenuRef}>
                            <button
                                onClick={() => setIsUtilityMenuOpen(!isUtilityMenuOpen)}
                                className={`p-2 rounded hover:bg-opacity-20 transition-all duration-200 ${darkMode ? 'text-white hover:bg-white' : 'text-black hover:bg-black hover:bg-opacity-20'}`}
                                aria-label="Utility menu"
                                aria-expanded={isUtilityMenuOpen}
                            >
                                <HiDotsVertical className="w-5 h-5" />
                            </button>

                            {isUtilityMenuOpen && (
                                <div className={`absolute right-0 top-full mt-2 py-2 w-56 rounded-lg shadow-xl z-50 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
                                    <LevelsNavDropDown isOnDarkTheme={darkMode} isMobile={false} />

                                    <button
                                        onClick={() => {
                                            setIsUtilityMenuOpen(false);
                                            handleNavigation('/my-profile');
                                        }}
                                        className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                    >
                                        My Profile
                                    </button>

                                    <button
                                        onClick={() => {
                                            setIsUtilityMenuOpen(false);
                                            handleSignOut();
                                        }}
                                        className="block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold hover:bg-darkOrangeColor hover:text-white"
                                    >
                                        Log Out
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <button onClick={() => handleNavigation('/login')} className="px-4 py-2 rounded hover:underline focus:outline-none font-semibold">Login</button>
                            <BtnParentFrame text="Sign Up" onClick={() => handleNavigation('/signup')} className={buttonTheme} />
                        </>
                    )}
                </div>

                {/* Mobile Coin Balance - pushed right, next to the menu button */}
                <div className="lg:hidden flex items-center ml-auto mr-3">
                    {user && user.coins !== undefined && (
                        <div
                            onClick={() => { if (!isAndroidApp()) handleNavigation('/get-coins'); }}
                            className="flex items-center cursor-pointer px-2 py-1 rounded border border-emerald-500 text-emerald-500 transition-all duration-200"
                        >
                            <FaCoins className="mr-1" size={14} />
                            <span className="text-sm font-medium">{user.coins}</span>
                        </div>
                    )}
                </div>

                {/* Hamburger Menu */}
                <div className="lg:hidden" ref={menuRef}>
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="p-4 max-sm:p-3"
                        aria-label="Toggle menu"
                        aria-expanded={isMenuOpen}
                    >
                        <svg className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                        </svg>
                    </button>

                    {isMenuOpen && (
                        <div className={`absolute right-0 top-full mt-2 py-2 w-48 max-sm:w-60 sm:max-lg:w-60 rounded-lg shadow-xl z-50 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
                            <button
                                onClick={() => handleNavigation('/home')}
                                className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                            >
                                Home
                            </button>
                            <button
                                onClick={() => handleNavigation('/kanji')}
                                className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                            >
                                Kanji
                            </button>
                            <button
                                onClick={() => handleNavigation('/kana-review')}
                                className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                            >
                                Kana
                            </button>
                            <button
                                onClick={() => handleNavigation('/immersion-hub/my-library')}
                                className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                            >
                                My Library
                            </button>
                            <button
                                onClick={() => handleNavigation('/leaderboards')}
                                className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                            >
                                Leaderboards
                            </button>
                            <button
                                onClick={() => handleNavigation('/my-stats')}
                                className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                            >
                                My Stats
                            </button>
                            {subscriptionStatus !== 'active' && !isAndroidApp() && (
                                <button
                                    onClick={() => handleNavigation('/pricing')}
                                    className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                >
                                    Pricing
                                </button>
                            )}
                            {user && user.coins !== undefined && !isAndroidApp() && (
                                <button
                                    onClick={() => handleNavigation('/get-coins')}
                                    className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                >
                                    <div className="flex items-center">
                                        <FaCoins className="mr-1 text-emerald-500" />
                                        Get Coins
                                    </div>
                                </button>
                            )}

                            {/* Levels and Utility Section */}
                            <div className={`border-t my-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
                            <LevelsNavDropDown isOnDarkTheme={darkMode} isMobile={true} />

                            {user && (
                                <>
                                    <button
                                        onClick={() => handleNavigation('/forum')}
                                        className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                    >
                                        Forum
                                    </button>
                                    <button
                                        onClick={() => handleNavigation('/my-profile')}
                                        className={`block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                    >
                                        My Profile
                                    </button>
                                    <button
                                        onClick={handleSignOut}
                                        className="block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold hover:bg-darkOrangeColor hover:text-white"
                                    >
                                        Log Out
                                    </button>
                                </>
                            )}

                            {isInstalledApp() && (
                                <div className={`mt-2 pt-2 px-4 pb-1 border-t text-xs ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                                    <div className="flex flex-wrap gap-x-3 gap-y-2">
                                        <a href="/about-us" className="py-1 hover:underline">About</a>
                                        <a href="https://community.onikanji.com/" className="py-1 hover:underline">Help</a>
                                        <a href="https://community.onikanji.com/t/contact-us" className="py-1 hover:underline">Contact</a>
                                        <a href="https://community.onikanji.com/t/privacy-policy" className="py-1 hover:underline">Privacy</a>
                                        <a href="https://community.onikanji.com/t/terms-of-service-and-use" className="py-1 hover:underline">Terms</a>
                                        <a href="https://discord.gg/7TEG9cqZTP" className="py-1 hover:underline">Discord</a>
                                    </div>
                                    <p className="mt-2">Made with ♥ by OniKanji</p>
                                </div>
                            )}

                            {!user && (
                                <>
                                    <button
                                        onClick={() => handleNavigation('/login')}
                                        className="block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold hover:bg-darkOrangeColor hover:text-white"
                                    >
                                        Login
                                    </button>
                                    <button
                                        onClick={() => handleNavigation('/signup')}
                                        className="block w-full text-left px-4 py-2 max-sm:py-3 sm:max-lg:py-3 font-semibold hover:bg-darkOrangeColor hover:text-white"
                                    >
                                        Sign Up
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
